import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  buildTextbookManuscriptImportPlan,
} from "../utils/textbookManuscriptImportAdapter";

import ChildrensBookManuscriptEditor
  from "./ChildrensBookManuscriptEditor";

const FRONT_MATTER_HEADINGS = {
  en: [
    "Title Page",
    "Copyright Page",
    "Dedication",
    "Epigraph",
    "Foreword",
    "Preface",
    "Acknowledgements",
    "Introduction",
    "Table of Contents",
    "List of Figures",
    "List of Tables",
    "Abbreviations",
  ],
  fr: [
    "Page de titre",
    "Page de copyright",
    "Dédicace",
    "Épigraphe",
    "Avant-propos",
    "Préface",
    "Remerciements",
    "Introduction",
    "Table des matières",
    "Liste des figures",
    "Liste des tableaux",
    "Abréviations",
  ],
};

const BACK_MATTER_HEADINGS = {
  en: [
    "Appendix",
    "Glossary",
    "Notes",
    "Endnotes",
    "Bibliography",
    "References",
    "Index",
    "About the Author",
    "Acknowledgements",
  ],
  fr: [
    "Annexe",
    "Glossaire",
    "Notes",
    "Notes de fin",
    "Bibliographie",
    "Références",
    "Index",
    "À propos de l’auteur",
    "Remerciements",
  ],
};

function matterHeadingOptions(
  type,
  isFR
) {
  const language =
    isFR ? "fr" : "en";

  return type ===
    "front_matter"
      ? FRONT_MATTER_HEADINGS[
          language
        ]
      : BACK_MATTER_HEADINGS[
          language
        ];
}

function matterHeadingPresetValue(
  item,
  isFR
) {
  const title =
    String(
      item?.title ?? ""
    ).trim();

  const options =
    matterHeadingOptions(
      item?.type,
      isFR
    );

  return options.includes(
    title
  )
    ? title
    : "__custom__";
}

const ITEM_TYPES =
  new Set([
    "front_matter",
    "part",
    "chapter",
    "section",
    "back_matter",
  ]);

function clean(value) {
  return String(value ?? "").trim();
}

function normalizeItems(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => ({
      itemId:
        clean(item?.itemId),
      type:
        clean(item?.type),
      title:
        String(
          item?.title ?? ""
        ),
      parentItemId:
        clean(
          item?.parentItemId
        ),

      ...(
        [
          "front_matter",
          "back_matter",
        ].includes(
          clean(
            item?.type
          )
        ) &&
        item?.manuscriptContent &&
        typeof item
          .manuscriptContent ===
          "object" &&
        !Array.isArray(
          item
            .manuscriptContent
        )
          ? {
              manuscriptContent:
                item
                  .manuscriptContent,
            }
          : {}
      ),
    }))
    .filter(
      (item) =>
        item.itemId &&
        ITEM_TYPES.has(
          item.type
        )
    );
}

function sameItems(left, right) {
  const a = normalizeItems(left);
  const b = normalizeItems(right);

  if (a.length !== b.length) {
    return false;
  }

  return a.every(
    (item, index) => {
      const other = b[index];

      return (
        item.itemId ===
          other.itemId &&
        item.type ===
          other.type &&
        item.title ===
          other.title &&
        item.parentItemId ===
          other.parentItemId &&
        JSON.stringify(
          item
            .manuscriptContent ||
          null
        ) ===
          JSON.stringify(
            other
              .manuscriptContent ||
            null
          )
      );
    }
  );
}

function newItemId() {
  return [
    "textbook",
    Date.now().toString(36),
    Math.random()
      .toString(36)
      .slice(2, 10),
  ].join("-");
}

function childrenOf(
  items,
  parentItemId
) {
  const parent =
    clean(parentItemId);

  return items.filter(
    (item) =>
      clean(
        item.parentItemId
      ) === parent
  );
}

function canonicalizeItems(value) {
  const items =
    normalizeItems(value);

  const byId =
    new Map(
      items.map(
        (item) => [
          item.itemId,
          item,
        ]
      )
    );

  const visited =
    new Set();

  const result = [];

  function appendTree(item) {
    if (
      !item ||
      visited.has(
        item.itemId
      )
    ) {
      return;
    }

    visited.add(
      item.itemId
    );

    result.push(item);

    childrenOf(
      items,
      item.itemId
    ).forEach(
      appendTree
    );
  }

  items
    .filter(
      (item) =>
        !item.parentItemId &&
        item.type ===
          "front_matter"
    )
    .forEach(appendTree);

  items
    .filter(
      (item) =>
        !item.parentItemId &&
        (
          item.type ===
            "part" ||
          item.type ===
            "chapter"
        )
    )
    .forEach(appendTree);

  items
    .filter(
      (item) =>
        !item.parentItemId &&
        item.type ===
          "back_matter"
    )
    .forEach(appendTree);

  items.forEach(
    (item) => {
      if (
        !visited.has(
          item.itemId
        ) &&
        (
          !item.parentItemId ||
          byId.has(
            item.parentItemId
          )
        )
      ) {
        appendTree(item);
      }
    }
  );

  return result;
}

function subtreeIds(
  items,
  itemId
) {
  const result =
    new Set();

  function visit(id) {
    if (
      result.has(id)
    ) {
      return;
    }

    result.add(id);

    childrenOf(
      items,
      id
    ).forEach(
      (child) =>
        visit(
          child.itemId
        )
    );
  }

  visit(itemId);
  return result;
}

function siblingItems(
  items,
  item
) {
  if (!item) {
    return [];
  }

  if (item.parentItemId) {
    return items.filter(
      (candidate) =>
        candidate
          .parentItemId ===
          item.parentItemId
    );
  }

  if (
    item.type ===
      "front_matter"
  ) {
    return items.filter(
      (candidate) =>
        !candidate
          .parentItemId &&
        candidate.type ===
          "front_matter"
    );
  }

  if (
    item.type ===
      "back_matter"
  ) {
    return items.filter(
      (candidate) =>
        !candidate
          .parentItemId &&
        candidate.type ===
          "back_matter"
    );
  }

  return items.filter(
    (candidate) =>
      !candidate
        .parentItemId &&
      (
        candidate.type ===
          "part" ||
        candidate.type ===
          "chapter"
      )
  );
}

function itemTypeLabel(
  type,
  isFR
) {
  if (
    type ===
      "front_matter"
  ) {
    return isFR
      ? "Pages liminaires"
      : "Front matter";
  }

  if (type === "part") {
    return isFR
      ? "Partie / Unité"
      : "Part / Unit";
  }

  if (
    type === "chapter"
  ) {
    return isFR
      ? "Chapitre"
      : "Chapter";
  }

  if (
    type === "section"
  ) {
    return "Section";
  }

  return isFR
    ? "Élément de fin"
    : "Back matter";
}

function nextTitle(
  items,
  type,
  parentItemId,
  isFR
) {
  const count =
    items.filter(
      (item) =>
        item.type === type &&
        clean(
          item.parentItemId
        ) ===
          clean(
            parentItemId
          )
    ).length + 1;

  if (
    type ===
      "front_matter"
  ) {
    return isFR
      ? `Pages liminaires ${count}`
      : `Front Matter ${count}`;
  }

  if (type === "part") {
    return isFR
      ? `Partie / Unité ${count}`
      : `Part / Unit ${count}`;
  }

  if (
    type === "chapter"
  ) {
    return isFR
      ? `Chapitre ${count}`
      : `Chapter ${count}`;
  }

  if (
    type === "section"
  ) {
    return `Section ${count}`;
  }

  return isFR
    ? `Élément de fin ${count}`
    : `Back Matter ${count}`;
}


function isGenericChapterTitle(
  value
) {
  return /^(chapter|chapitre)\s+\d+(?:\s+\((copy|copie)\))?$/i.test(
    clean(value)
  );
}

function defaultChapterTitleForOrdinal(
  ordinal,
  isFR
) {
  return isFR
    ? `Chapitre ${ordinal}`
    : `Chapter ${ordinal}`;
}

function normalizeGenericChapterTitles(
  value,
  isFR
) {
  const normalized =
    canonicalizeItems(
      value
    );

  let chapterOrdinal = 0;

  return normalized.map(
    (item) => {
      if (
        item.type !==
          "chapter"
      ) {
        return item;
      }

      chapterOrdinal += 1;

      if (
        !isGenericChapterTitle(
          item.title
        )
      ) {
        return item;
      }

      const title =
        defaultChapterTitleForOrdinal(
          chapterOrdinal,
          isFR
        );

      return item.title ===
        title
        ? item
        : {
            ...item,
            title,
          };
    }
  );
}

function chapterNavigatorOptionLabel(
  chapter,
  index,
  isFR
) {
  const ordinal =
    index + 1;

  const structuralLabel =
    defaultChapterTitleForOrdinal(
      ordinal,
      isFR
    );

  const title =
    clean(
      chapter?.title
    );

  if (
    !title ||
    isGenericChapterTitle(
      title
    )
  ) {
    return structuralLabel;
  }

  return `${structuralLabel} · ${title}`;
}

export default function TextbookBookMap({
  project,
  projectPlan,
  loading = false,
  loadError = "",
  onReload,
  onSave,
  onPreviewManuscriptImport,
  onApplyManuscriptImport,
  onOpenChapter,
  canEdit = true,
  isFR = false,
}) {
  const persistedItems =
    useMemo(
      () =>
        normalizeGenericChapterTitles(
          projectPlan
            ?.bookMap
            ?.items,
          isFR
        ),
      [
        projectPlan
          ?.bookMap
          ?.items,
        isFR,
      ]
    );

  const [items, setItems] =
    useState(
      persistedItems
    );

  const [saving, setSaving] =
    useState(false);

  const [saveError, setSaveError] =
    useState("");

  const [saveMessage, setSaveMessage] =
    useState("");

  const [
    pendingRemoveId,
    setPendingRemoveId,
  ] = useState("");

  const [
    movePositionItemId,
    setMovePositionItemId,
  ] = useState("");

  const [
    movePositionValue,
    setMovePositionValue,
  ] = useState("");

  const [
    frontMatterEditing,
    setFrontMatterEditing,
  ] = useState(false);

  const [
    backMatterEditing,
    setBackMatterEditing,
  ] = useState(false);

  const [
    activeMainItemId,
    setActiveMainItemId,
  ] = useState("");

  const [
    chapterEditSnapshot,
    setChapterEditSnapshot,
  ] = useState(null);

  const [
    chapterNavigatorId,
    setChapterNavigatorId,
  ] = useState("");

  const pendingChapterScrollId =
    useRef("");

  const [
    matterEditorItemId,
    setMatterEditorItemId,
  ] = useState("");

  const [
    matterDetailsItemId,
    setMatterDetailsItemId,
  ] = useState("");

  const [
    manuscriptImportFile,
    setManuscriptImportFile,
  ] = useState(null);

  const [
    manuscriptImportPreviewing,
    setManuscriptImportPreviewing,
  ] = useState(false);

  const [
    manuscriptImportResult,
    setManuscriptImportResult,
  ] = useState(null);

  const [
    manuscriptImportError,
    setManuscriptImportError,
  ] = useState("");

  useEffect(() => {
    setItems(
      persistedItems
    );
    setSaveError("");
    setSaveMessage("");
    setPendingRemoveId("");
    setMovePositionItemId("");
    setMovePositionValue("");
    setFrontMatterEditing(false);
    setBackMatterEditing(false);
    setActiveMainItemId("");
    setChapterEditSnapshot(null);
    setChapterNavigatorId("");
    pendingChapterScrollId.current =
      "";
    setMatterEditorItemId("");
    setMatterDetailsItemId("");
  }, [persistedItems]);

  const revision =
    Number(
      projectPlan
        ?.revision || 0
    );

  const dirty =
    !sameItems(
      items,
      persistedItems
    );

  const allChapters =
    useMemo(
      () =>
        items.filter(
          (item) =>
            item.type ===
              "chapter"
        ),
      [items]
    );

  const currentNavigatorIndex =
    allChapters.findIndex(
      (chapter) =>
        chapter.itemId ===
        chapterNavigatorId
    );

  useEffect(() => {
    if (
      allChapters.length === 0
    ) {
      if (chapterNavigatorId) {
        setChapterNavigatorId("");
      }
      return;
    }

    if (
      !allChapters.some(
        (chapter) =>
          chapter.itemId ===
            chapterNavigatorId
      )
    ) {
      setChapterNavigatorId(
        allChapters[0].itemId
      );
    }
  }, [
    allChapters,
    chapterNavigatorId,
  ]);

  useEffect(() => {
    const targetId =
      clean(
        pendingChapterScrollId
          .current
      );

    if (!targetId) {
      return;
    }

    const target =
      typeof document !==
        "undefined"
        ? document.getElementById(
            `textbook-book-structure-chapter-${targetId}`
          )
        : null;

    if (!target) {
      return;
    }

    pendingChapterScrollId.current =
      "";

    target.scrollIntoView?.({
      behavior: "smooth",
      block: "center",
    });
  }, [
    items,
    chapterNavigatorId,
  ]);

  const manuscriptImportPlan =
    manuscriptImportResult
      ?.value || null;

  const manuscriptImportItems =
    Array.isArray(
      manuscriptImportPlan
        ?.bookMap
        ?.items
    )
      ? manuscriptImportPlan
          .bookMap
          .items
      : [];

  function clearSaveState() {
    setSaveError("");
    setSaveMessage("");
  }

  function beginChapterEdit(
    itemId
  ) {
    if (!canEdit) {
      return;
    }

    const current =
      canonicalizeItems(
        items
      );

    const editIds =
      subtreeIds(
        current,
        itemId
      );

    const subtreeItems =
      current.filter(
        (candidate) =>
          editIds.has(
            candidate.itemId
          )
      );

    setChapterEditSnapshot({
      itemId,
      subtreeItems,
    });

    setActiveMainItemId(
      itemId
    );

    setChapterNavigatorId(
      itemId
    );

    setPendingRemoveId("");
    setMovePositionItemId("");
    setMovePositionValue("");
  }

  function finishChapterEdit(
    itemId
  ) {
    if (
      activeMainItemId !==
        itemId
    ) {
      return;
    }

    setActiveMainItemId("");
    setChapterEditSnapshot(null);
    setPendingRemoveId("");
    setMovePositionItemId("");
    setMovePositionValue("");
  }

  function cancelChapterEdit(
    itemId
  ) {
    if (
      activeMainItemId !==
        itemId
    ) {
      return;
    }

    if (
      chapterEditSnapshot &&
      chapterEditSnapshot.itemId ===
        itemId &&
      Array.isArray(
        chapterEditSnapshot
          .subtreeItems
      )
    ) {
      setItems(
        (currentValue) => {
          const current =
            canonicalizeItems(
              currentValue
            );

          const chapterIndex =
            current.findIndex(
              (candidate) =>
                candidate.itemId ===
                itemId
            );

          if (
            chapterIndex < 0
          ) {
            return current;
          }

          const currentEditIds =
            subtreeIds(
              current,
              itemId
            );

          const stripped =
            current.filter(
              (candidate) =>
                !currentEditIds.has(
                  candidate.itemId
                )
            );

          const restoredSubtree =
            canonicalizeItems(
              chapterEditSnapshot
                .subtreeItems
            );

          return canonicalizeItems([
            ...stripped.slice(
              0,
              chapterIndex
            ),
            ...restoredSubtree,
            ...stripped.slice(
              chapterIndex
            ),
          ]);
        }
      );
    }

    setActiveMainItemId("");
    setChapterEditSnapshot(null);
    setPendingRemoveId("");
    setMovePositionItemId("");
    setMovePositionValue("");
    clearSaveState();
  }

  function jumpToBookStructureChapter(
    itemId
  ) {
    const target =
      allChapters.find(
        (chapter) =>
          chapter.itemId ===
            itemId
      );

    if (!target) {
      return;
    }

    setChapterNavigatorId(
      itemId
    );

    pendingChapterScrollId.current =
      itemId;
  }

  function stepBookStructureChapter(
    direction
  ) {
    if (
      allChapters.length === 0
    ) {
      return;
    }

    const baseIndex =
      currentNavigatorIndex >= 0
        ? currentNavigatorIndex
        : 0;

    const targetIndex =
      baseIndex +
      direction;

    if (
      targetIndex < 0 ||
      targetIndex >=
        allChapters.length
    ) {
      return;
    }

    jumpToBookStructureChapter(
      allChapters[
        targetIndex
      ].itemId
    );
  }

  function chooseWordManuscript(
    event
  ) {
    const file =
      event?.target
        ?.files?.[0] ||
      null;

    setManuscriptImportFile(
      file
    );
    setManuscriptImportResult(
      null
    );
    setManuscriptImportError(
      ""
    );
  }

  async function previewWordManuscript() {
    if (
      !canEdit ||
      manuscriptImportPreviewing
    ) {
      return;
    }

    if (!manuscriptImportFile) {
      setManuscriptImportError(
        isFR
          ? "Choisissez d’abord un manuscrit Word DOCX."
          : "Choose a DOCX Word manuscript first."
      );
      return;
    }

    if (
      typeof onPreviewManuscriptImport !==
        "function"
    ) {
      setManuscriptImportError(
        isFR
          ? "L’aperçu du manuscrit n’est pas disponible pour le moment."
          : "Manuscript preview is not available right now."
      );
      return;
    }

    try {
      setManuscriptImportPreviewing(
        true
      );
      setManuscriptImportError(
        ""
      );
      setManuscriptImportResult(
        null
      );

      const preview =
        await onPreviewManuscriptImport(
          manuscriptImportFile
        );

      const result =
        buildTextbookManuscriptImportPlan(
          preview
        );

      if (
        !result?.ok ||
        !result?.value
      ) {
        throw new Error(
          Array.isArray(
            result?.errors
          )
            ? result.errors
                .filter(Boolean)
                .join(" ")
            : ""
        );
      }

      setManuscriptImportResult(
        result
      );
    } catch (error) {
      setManuscriptImportError(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible de prévisualiser ce manuscrit Word."
              : "Could not preview this Word manuscript."
          )
      );
    } finally {
      setManuscriptImportPreviewing(
        false
      );
    }
  }

  function addItem(
    type,
    parentItemId = ""
  ) {
    if (!canEdit) {
      return;
    }

    clearSaveState();

    const itemId =
      newItemId();

    setItems(
      (current) => {
        const next = [
          ...current,
          {
            itemId,
            type,
            title:
              nextTitle(
                current,
                type,
                parentItemId,
                isFR
              ),
            parentItemId:
              clean(
                parentItemId
              ),
          },
        ];

        return normalizeGenericChapterTitles(
          next,
          isFR
        );
      }
    );

    if (
      type === "section" &&
      clean(parentItemId)
    ) {
      setActiveMainItemId(
        clean(parentItemId)
      );
    } else if (
      type === "part"
    ) {
      setActiveMainItemId(
        itemId
      );
    } else if (
      type === "chapter"
    ) {
      setActiveMainItemId("");
      setChapterEditSnapshot(null);
      setChapterNavigatorId(
        itemId
      );
      pendingChapterScrollId.current =
        itemId;
    }
  }

  function updateTitle(
    itemId,
    title
  ) {
    if (!canEdit) {
      return;
    }

    clearSaveState();

    setItems(
      (current) =>
        current.map(
          (item) =>
            item.itemId ===
            itemId
              ? {
                  ...item,
                  title,
                }
              : item
        )
    );
  }

  function updateMatterContent(
    itemId,
    editorValue
  ) {
    if (!canEdit) {
      return;
    }

    const manuscriptContent =
      editorValue
        ?.manuscriptContent;

    if (
      !manuscriptContent ||
      typeof manuscriptContent !==
        "object" ||
      Array.isArray(
        manuscriptContent
      )
    ) {
      return;
    }

    clearSaveState();

    setItems(
      (current) =>
        current.map(
          (item) =>
            item.itemId ===
              itemId &&
            [
              "front_matter",
              "back_matter",
            ].includes(
              item.type
            )
              ? {
                  ...item,
                  manuscriptContent,
                }
              : item
        )
    );
  }


  function requestRemove(
    itemId
  ) {
    if (!canEdit) {
      return;
    }

    clearSaveState();

    setPendingRemoveId(
      (current) =>
        current === itemId
          ? ""
          : itemId
    );
  }

  function confirmRemove(
    itemId
  ) {
    if (!canEdit) {
      return;
    }

    clearSaveState();

    setItems(
      (current) => {
        const removal =
          subtreeIds(
            current,
            itemId
          );

        return normalizeGenericChapterTitles(
          current.filter(
            (item) =>
              !removal.has(
                item.itemId
              )
          ),
          isFR
        );
      }
    );

    setPendingRemoveId(
      ""
    );

    if (
      activeMainItemId &&
      subtreeIds(
        items,
        itemId
      ).has(
        activeMainItemId
      )
    ) {
      setActiveMainItemId("");
    }
  }

  function moveItem(
    itemId,
    direction
  ) {
    if (!canEdit) {
      return;
    }

    clearSaveState();

    setItems(
      (currentValue) => {
        const current =
          canonicalizeItems(
            currentValue
          );

        const item =
          current.find(
            (candidate) =>
              candidate.itemId ===
              itemId
          );

        if (!item) {
          return current;
        }

        const siblings =
          siblingItems(
            current,
            item
          );

        const siblingIndex =
          siblings.findIndex(
            (candidate) =>
              candidate.itemId ===
              itemId
          );

        const targetIndex =
          siblingIndex +
          direction;

        if (
          siblingIndex < 0 ||
          targetIndex < 0 ||
          targetIndex >=
            siblings.length
        ) {
          return current;
        }

        const target =
          siblings[
            targetIndex
          ];

        const currentIds =
          subtreeIds(
            current,
            item.itemId
          );

        const targetIds =
          subtreeIds(
            current,
            target.itemId
          );

        const currentBlock =
          current.filter(
            (candidate) =>
              currentIds.has(
                candidate.itemId
              )
          );

        const targetBlock =
          current.filter(
            (candidate) =>
              targetIds.has(
                candidate.itemId
              )
          );

        const firstCurrent =
          current.findIndex(
            (candidate) =>
              currentIds.has(
                candidate.itemId
              )
          );

        const firstTarget =
          current.findIndex(
            (candidate) =>
              targetIds.has(
                candidate.itemId
              )
          );

        const stripped =
          current.filter(
            (candidate) =>
              !currentIds.has(
                candidate.itemId
              ) &&
              !targetIds.has(
                candidate.itemId
              )
          );

        const insertAt =
          Math.min(
            firstCurrent,
            firstTarget
          );

        const replacement =
          direction < 0
            ? [
                ...currentBlock,
                ...targetBlock,
              ]
            : [
                ...targetBlock,
                ...currentBlock,
              ];

        const next = [
          ...stripped.slice(
            0,
            insertAt
          ),
          ...replacement,
          ...stripped.slice(
            insertAt
          ),
        ];

        return normalizeGenericChapterTitles(
          next,
          isFR
        );
      }
    );
  }

  function canMove(
    item,
    direction
  ) {
    const siblings =
      siblingItems(
        items,
        item
      );

    const index =
      siblings.findIndex(
        (candidate) =>
          candidate.itemId ===
          item.itemId
      );

    const target =
      index + direction;

    return (
      index >= 0 &&
      target >= 0 &&
      target <
        siblings.length
    );
  }

  function moveItemToPosition(
    itemId,
    requestedPosition
  ) {
    if (!canEdit) {
      return false;
    }

    const current =
      canonicalizeItems(
        items
      );

    const item =
      current.find(
        (candidate) =>
          candidate.itemId ===
          itemId
      );

    if (
      !item ||
      item.type !== "chapter"
    ) {
      return false;
    }

    const siblings =
      siblingItems(
        current,
        item
      );

    const sourceIndex =
      siblings.findIndex(
        (candidate) =>
          candidate.itemId ===
          itemId
      );

    const position =
      Number.parseInt(
        String(
          requestedPosition || ""
        ),
        10
      );

    if (
      sourceIndex < 0 ||
      !Number.isInteger(
        position
      ) ||
      position < 1 ||
      position >
        siblings.length
    ) {
      return false;
    }

    const targetIndex =
      position - 1;

    if (
      targetIndex ===
      sourceIndex
    ) {
      setMovePositionItemId("");
      setMovePositionValue("");
      return true;
    }

    const sourceIds =
      subtreeIds(
        current,
        itemId
      );

    const sourceBlock =
      current.filter(
        (candidate) =>
          sourceIds.has(
            candidate.itemId
          )
      );

    const stripped =
      current.filter(
        (candidate) =>
          !sourceIds.has(
            candidate.itemId
          )
      );

    const remainingSiblings =
      siblings.filter(
        (candidate) =>
          candidate.itemId !==
          itemId
      );

    let insertAt =
      stripped.length;

    if (
      targetIndex <
      remainingSiblings.length
    ) {
      const target =
        remainingSiblings[
          targetIndex
        ];

      insertAt =
        stripped.findIndex(
          (candidate) =>
            candidate.itemId ===
            target.itemId
        );

      if (insertAt < 0) {
        return false;
      }
    } else if (
      remainingSiblings.length >
      0
    ) {
      const lastSibling =
        remainingSiblings[
          remainingSiblings.length -
            1
        ];

      const lastIds =
        subtreeIds(
          stripped,
          lastSibling.itemId
        );

      const lastIndex =
        stripped.reduce(
          (
            result,
            candidate,
            index
          ) =>
            lastIds.has(
              candidate.itemId
            )
              ? index
              : result,
          -1
        );

      insertAt =
        lastIndex >= 0
          ? lastIndex + 1
          : stripped.length;
    }

    const next = [
      ...stripped.slice(
        0,
        insertAt
      ),
      ...sourceBlock,
      ...stripped.slice(
        insertAt
      ),
    ];

    clearSaveState();

    setItems(
      normalizeGenericChapterTitles(
        next,
        isFR
      )
    );

    setMovePositionItemId("");
    setMovePositionValue("");

    return true;
  }


  function duplicateChapterStructure(
    itemId
  ) {
    if (!canEdit) {
      return;
    }

    const current =
      canonicalizeItems(
        items
      );

    const chapter =
      current.find(
        (candidate) =>
          candidate.itemId ===
          itemId &&
          candidate.type ===
          "chapter"
      );

    if (!chapter) {
      return;
    }

    const sourceIds =
      subtreeIds(
        current,
        itemId
      );

    const sourceBlock =
      current.filter(
        (candidate) =>
          sourceIds.has(
            candidate.itemId
          )
      );

    const idMap =
      new Map();

    sourceBlock.forEach(
      (candidate) =>
        idMap.set(
          candidate.itemId,
          newItemId()
        )
    );

    const duplicateBlock =
      sourceBlock.map(
        (candidate) => ({
          ...candidate,

          itemId:
            idMap.get(
              candidate.itemId
            ),

          parentItemId:
            idMap.get(
              candidate.parentItemId
            ) ||
            candidate.parentItemId,

          title:
            candidate.itemId ===
              itemId
              ? (
                  clean(
                    candidate.title
                  )
                    ? `${candidate.title} ${
                        isFR
                          ? "(copie)"
                          : "(copy)"
                      }`
                    : (
                        isFR
                          ? "Chapitre (copie)"
                          : "Chapter (copy)"
                      )
                )
              : candidate.title,
        })
      );

    const lastSourceIndex =
      current.reduce(
        (
          result,
          candidate,
          index
        ) =>
          sourceIds.has(
            candidate.itemId
          )
            ? index
            : result,
        -1
      );

    const insertAt =
      Math.max(
        0,
        lastSourceIndex + 1
      );

    const next = [
      ...current.slice(
        0,
        insertAt
      ),
      ...duplicateBlock,
      ...current.slice(
        insertAt
      ),
    ];

    const newChapterId =
      idMap.get(
        itemId
      );

    clearSaveState();

    setItems(
      normalizeGenericChapterTitles(
        next,
        isFR
      )
    );

    setActiveMainItemId("");
    setChapterEditSnapshot(null);
    setChapterNavigatorId(
      newChapterId
    );
    pendingChapterScrollId.current =
      newChapterId;

    setPendingRemoveId("");
    setMovePositionItemId("");
    setMovePositionValue("");
  }

  async function applyWordManuscriptImport() {
    if (
      !canEdit ||
      saving ||
      dirty ||
      revision < 1 ||
      typeof onApplyManuscriptImport !==
        "function" ||
      !manuscriptImportPlan ||
      manuscriptImportPlan
        .ready !== true ||
      manuscriptImportPlan
        .leadingContent
    ) {
      return;
    }

    const importedItems =
      canonicalizeItems(
        manuscriptImportItems
      );

    const chapterDrafts =
      Array.isArray(
        manuscriptImportPlan
          ?.chapterDrafts
      )
        ? manuscriptImportPlan
            .chapterDrafts
        : [];

    if (
      importedItems.length === 0 ||
      chapterDrafts.length === 0
    ) {
      setSaveError(
        isFR
          ? "Aucun chapitre prêt à être ajouté n’a été trouvé dans cette prévisualisation."
          : "No ready Chapters were found in this preview."
      );
      setSaveMessage("");
      return;
    }

    const existingIds =
      new Set(
        persistedItems.map(
          (item) =>
            item.itemId
        )
      );

    const collision =
      importedItems.find(
        (item) =>
          existingIds.has(
            item.itemId
          )
      );

    if (collision) {
      setSaveError(
        isFR
          ? "Cette prévisualisation n’est plus à jour. Prévisualisez de nouveau le manuscrit avant de l’ajouter."
          : "This preview is no longer current. Preview the manuscript again before adding it."
      );
      setSaveMessage("");
      return;
    }

    const mergedItems =
      canonicalizeItems([
        ...persistedItems,
        ...importedItems,
      ]);

    try {
      setSaving(true);
      setSaveError("");
      setSaveMessage("");

      const result =
        await onApplyManuscriptImport(
          {
            bookMap: {
              items:
                mergedItems,
            },

            previousBookMap: {
              items:
                persistedItems,
            },

            chapterDrafts,
          },
          revision
        );

      const nextPlan =
        result?.projectPlan ||
        null;

      if (!nextPlan) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné la Structure du livre après l’importation."
            : "The server did not return the Book Structure after the import."
        );
      }

      setItems(
        canonicalizeItems(
          nextPlan
            ?.bookMap
            ?.items
        )
      );

      setManuscriptImportResult(
        null
      );

      setManuscriptImportFile(
        null
      );

      const chapterCount =
        Number(
          result
            ?.chapterDraftCount ||
          manuscriptImportPlan
            ?.summary
            ?.chapterCount ||
          0
        );

      setSaveMessage(
        isFR
          ? (
              chapterCount === 1
                ? "Manuscrit ajouté : 1 chapitre."
                : `Manuscrit ajouté : ${chapterCount} chapitres.`
            )
          : (
              chapterCount === 1
                ? "Manuscript added: 1 Chapter."
                : `Manuscript added: ${chapterCount} Chapters.`
            )
      );
    } catch (error) {
      setSaveError(
        clean(
          error?.message
        ) ||
        (
          isFR
            ? "Impossible d’ajouter le manuscrit."
            : "Could not add the manuscript."
        )
      );

      setSaveMessage("");
    } finally {
      setSaving(false);
    }
  }

  async function saveCurrentBookStructure({
    showSavedMessage = true,
  } = {}) {
    if (
      !canEdit ||
      saving ||
      revision < 1 ||
      typeof onSave !==
        "function"
    ) {
      return !dirty;
    }

    if (!dirty) {
      return true;
    }

    const normalized =
      canonicalizeItems(
        items
      );

    if (
      normalized.some(
        (item) =>
          !clean(
            item.title
          )
      )
    ) {
      setSaveError(
        isFR
          ? "Donnez un titre à chaque élément de la Structure du livre avant d’enregistrer."
          : "Give every Book Structure item a title before saving."
      );
      setSaveMessage("");
      return false;
    }

    try {
      setSaving(true);
      setSaveError("");
      setSaveMessage("");

      await onSave(
        {
          items:
            normalized,
        },
        revision
      );

      if (showSavedMessage) {
        setSaveMessage(
          isFR
            ? "Enregistré."
            : "Saved."
        );
      }

      return true;
    } catch (error) {
      setSaveError(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible d’enregistrer la Structure du livre."
              : "Could not save Book Structure."
          )
      );
      setSaveMessage("");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function openChapterWithStructureSave(
    itemId
  ) {
    if (
      saving ||
      typeof onOpenChapter !==
        "function"
    ) {
      return;
    }

    if (dirty) {
      const saved =
        await saveCurrentBookStructure({
          showSavedMessage:
            false,
        });

      if (!saved) {
        return;
      }
    }

    onOpenChapter(
      itemId
    );
  }

  async function submit(
    event
  ) {
    event?.preventDefault?.();

    await saveCurrentBookStructure();
  }

  if (
    loading ||
    (
      !projectPlan &&
      !loadError
    )
  ) {
    return (
      <section
        className="ns-card"
        style={{
          padding: 22,
          borderRadius: 20,
        }}
      >
        <strong>
          {isFR
            ? "Ouverture de la Structure du livre…"
            : "Opening Book Structure…"}
        </strong>
      </section>
    );
  }

  if (!projectPlan) {
    return (
      <section
        className="ns-card"
        style={{
          padding: 22,
          borderRadius: 20,
        }}
      >
        <p
          style={{
            marginTop: 0,
            color: "#b91c1c",
            fontWeight: 800,
          }}
        >
          {loadError ||
            (
              isFR
                ? "La Structure du livre n’a pas pu être ouverte."
                : "Book Structure could not be opened."
            )}
        </p>

        {typeof onReload ===
        "function" ? (
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onReload}
          >
            {isFR
              ? "Réessayer"
              : "Try again"}
          </button>
        ) : null}
      </section>
    );
  }

  const frontMatter =
    items.filter(
      (item) =>
        !item.parentItemId &&
        item.type ===
          "front_matter"
    );

  const mainRoots =
    items.filter(
      (item) =>
        !item.parentItemId &&
        (
          item.type ===
            "part" ||
          item.type ===
            "chapter"
        )
    );

  const backMatter =
    items.filter(
      (item) =>
        !item.parentItemId &&
        item.type ===
          "back_matter"
    );

  const persistedFrontMatter =
    persistedItems.filter(
      (item) =>
        !item.parentItemId &&
        item.type ===
          "front_matter"
    );

  const persistedBackMatter =
    persistedItems.filter(
      (item) =>
        !item.parentItemId &&
        item.type ===
          "back_matter"
    );

  const frontMatterDirty =
    !sameItems(
      frontMatter,
      persistedFrontMatter
    );

  const backMatterDirty =
    !sameItems(
      backMatter,
      persistedBackMatter
    );

  function renderItem(
    item,
    depth = 0
  ) {
    const isMatter =
      [
        "front_matter",
        "back_matter",
      ].includes(
        item.type
      );

    const hasMatterContent =
      isMatter &&
      Array.isArray(
        item
          ?.manuscriptContent
          ?.blocks
      ) &&
      item
        .manuscriptContent
        .blocks
        .some(
          (block) =>
            Array.isArray(
              block?.runs
            ) &&
            block.runs.some(
              (run) =>
                clean(
                  run?.text
                )
            )
        );

    const children =
      childrenOf(
        items,
        item.itemId
      );

    if (isMatter) {
      const matterPeers =
        items.filter(
          (candidate) =>
            candidate.type ===
              item.type &&
            !candidate.parentItemId
        );

      const matterOrdinal =
        Math.max(
          1,
          matterPeers.findIndex(
            (candidate) =>
              candidate.itemId ===
                item.itemId
          ) + 1
        );

      const matterCount =
        matterPeers.length;

      const matterDetailsOpen =
        matterDetailsItemId ===
          item.itemId;

      const matterTitle =
        clean(
          item.title
        ) ||
        (
          item.type ===
            "front_matter"
            ? (
                isFR
                  ? "Pages liminaires sans titre"
                  : "Untitled Front Matter"
              )
            : (
                isFR
                  ? "Élément de fin sans titre"
                  : "Untitled Back Matter"
              )
        );

      const matterLabel =
        item.type ===
          "front_matter"
          ? (
              isFR
                ? `PAGES LIMINAIRES ${matterOrdinal} SUR ${matterCount}`
                : `FRONT MATTER ${matterOrdinal} OF ${matterCount}`
            )
          : (
              isFR
                ? `ÉLÉMENT DE FIN ${matterOrdinal} SUR ${matterCount}`
                : `BACK MATTER ${matterOrdinal} OF ${matterCount}`
            );

      const matterAccent =
        item.type ===
          "front_matter"
          ? "#047857"
          : "#475569";

      return (
        <article
          key={item.itemId}
          data-textbook-matter-card={
            item.itemId
          }
          data-textbook-matter-type={
            item.type
          }
          data-textbook-matter-details-open={
            matterDetailsOpen
              ? "true"
              : "false"
          }
          className="ns-card"
          style={{
            padding: 16,
            borderRadius: 16,
            border:
              "1.5px solid rgba(100,116,139,0.26)",
            borderLeft:
              `6px solid ${matterAccent}`,
            background:
              "#ffffff",
            boxShadow:
              "0 8px 20px rgba(15,23,42,0.07)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems:
                "flex-start",
              justifyContent:
                "space-between",
              gap: 14,
              flexWrap:
                "wrap",
            }}
          >
            <div
              style={{
                flex:
                  "1 1 300px",
                minWidth: 0,
              }}
            >
              <div
                data-textbook-matter-number={
                  matterOrdinal
                }
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing:
                    "0.08em",
                  textTransform:
                    "uppercase",
                  color:
                    matterAccent,
                }}
              >
                {matterLabel}
              </div>

              <strong
                style={{
                  display:
                    "block",
                  marginTop: 4,
                  fontSize: 17,
                  lineHeight: 1.35,
                  color: "#0f172a",
                }}
              >
                {matterTitle}
              </strong>

              <div
                className="ns-card-meta"
                data-textbook-matter-content-status={
                  hasMatterContent
                    ? "present"
                    : "empty"
                }
                style={{
                  marginTop: 5,
                  lineHeight: 1.4,
                  color:
                    hasMatterContent
                      ? "#166534"
                      : "#64748b",
                  fontWeight: 750,
                }}
              >
                {hasMatterContent
                  ? (
                      isFR
                        ? "Contenu enrichi présent"
                        : "Rich content present"
                    )
                  : (
                      isFR
                        ? "Aucun contenu pour le moment"
                        : "No content yet"
                    )}
              </div>
            </div>

            {canEdit ? (
              <div
                style={{
                  display: "flex",
                  gap: 7,
                  flexWrap:
                    "wrap",
                  alignItems:
                    "center",
                  justifyContent:
                    "flex-end",
                }}
              >
                <button
                  className="ns-button ns-button-primary"
                  type="button"
                  disabled={saving}
                  onClick={() =>
                    setMatterEditorItemId(
                      item.itemId
                    )
                  }
                >
                  {hasMatterContent
                    ? (
                        isFR
                          ? "Modifier le contenu"
                          : "Edit Content"
                      )
                    : (
                        isFR
                          ? "Écrire le contenu"
                          : "Write Content"
                      )}
                </button>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={saving}
                  aria-expanded={
                    matterDetailsOpen
                  }
                  onClick={() =>
                    setMatterDetailsItemId(
                      matterDetailsOpen
                        ? ""
                        : item.itemId
                    )
                  }
                >
                  {matterDetailsOpen
                    ? (
                        isFR
                          ? "Masquer les détails"
                          : "Hide Details"
                      )
                    : (
                        isFR
                          ? "Modifier les détails"
                          : "Edit Details"
                      )}
                </button>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    saving ||
                    !canMove(
                      item,
                      -1
                    )
                  }
                  onClick={() =>
                    moveItem(
                      item.itemId,
                      -1
                    )
                  }
                >
                  {isFR
                    ? "Plus tôt"
                    : "Move Earlier"}
                </button>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    saving ||
                    !canMove(
                      item,
                      1
                    )
                  }
                  onClick={() =>
                    moveItem(
                      item.itemId,
                      1
                    )
                  }
                >
                  {isFR
                    ? "Plus tard"
                    : "Move Later"}
                </button>

                {pendingRemoveId ===
                item.itemId ? (
                  <>
                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      disabled={saving}
                      onClick={() =>
                        setPendingRemoveId(
                          ""
                        )
                      }
                    >
                      {isFR
                        ? "Annuler"
                        : "Cancel"}
                    </button>

                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      disabled={saving}
                      onClick={() =>
                        confirmRemove(
                          item.itemId
                        )
                      }
                      style={{
                        color:
                          "#b91c1c",
                        borderColor:
                          "rgba(185,28,28,0.34)",
                      }}
                    >
                      {isFR
                        ? "Confirmer"
                        : "Confirm Remove"}
                    </button>
                  </>
                ) : (
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      requestRemove(
                        item.itemId
                      )
                    }
                  >
                    {isFR
                      ? "Retirer"
                      : "Remove"}
                  </button>
                )}
              </div>
            ) : null}
          </div>

          {matterDetailsOpen ? (
            <div
              data-textbook-matter-details-panel="true"
              style={{
                marginTop: 14,
                padding:
                  "13px 14px",
                borderRadius: 13,
                border:
                  "1px solid rgba(148,163,184,0.26)",
                background:
                  "rgba(248,250,252,0.88)",
                display: "grid",
                gap: 9,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  color: "#475569",
                  textTransform:
                    "uppercase",
                  letterSpacing:
                    "0.06em",
                }}
              >
                {isFR
                  ? "RUBRIQUE ET TITRE"
                  : "HEADING & TITLE"}
              </div>

              <select
                className="ns-input"
                aria-label={
                  isFR
                    ? "Choisir une rubrique"
                    : "Choose a matter heading"
                }
                value={
                  matterHeadingPresetValue(
                    item,
                    isFR
                  )
                }
                disabled={
                  saving ||
                  !canEdit
                }
                onChange={(event) => {
                  const value =
                    event.target.value;

                  if (
                    value &&
                    value !==
                      "__custom__"
                  ) {
                    updateTitle(
                      item.itemId,
                      value
                    );
                  }
                }}
              >
                {matterHeadingOptions(
                  item.type,
                  isFR
                ).map(
                  (heading) => (
                    <option
                      key={heading}
                      value={heading}
                    >
                      {heading}
                    </option>
                  )
                )}

                <option value="__custom__">
                  {isFR
                    ? "Titre personnalisé…"
                    : "Custom heading…"}
                </option>
              </select>

              <input
                className="ns-input"
                value={item.title}
                disabled={
                  saving ||
                  !canEdit
                }
                onChange={(event) =>
                  updateTitle(
                    item.itemId,
                    event.target
                      .value
                  )
                }
                aria-label={
                  isFR
                    ? `${matterLabel} : titre`
                    : `${matterLabel} title`
                }
              />

              <div
                className="ns-card-meta"
                style={{
                  lineHeight: 1.4,
                }}
              >
                {isFR
                  ? "Choisissez une rubrique courante ou saisissez votre propre titre. Les autres cartes restent compactes."
                  : "Choose a common book heading or enter your own title. Other Matter cards stay compact."}
              </div>
            </div>
          ) : null}
        </article>
      );
    }

    return (
      <article
        key={item.itemId}
        className="ns-card"
        style={{
          padding:
            depth > 0
              ? 14
              : 16,
          borderRadius: 16,
          border:
            depth > 0
              ? "1px solid rgba(15,23,42,0.12)"
              : "1.5px solid rgba(5,150,105,0.18)",
          background:
            depth > 0
              ? "#ffffff"
              : "rgba(255,255,255,0.98)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems:
              "flex-start",
            justifyContent:
              "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <label
            style={{
              flex: "1 1 260px",
              display: "grid",
              gap: 6,
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 900,
                color: "#047857",
                textTransform:
                  "uppercase",
                letterSpacing:
                  "0.06em",
              }}
            >
              {
                itemTypeLabel(
                  item.type,
                  isFR
                )
              }
            </span>

            {isMatter ? (
              <>
                <span
                  className="ns-card-meta"
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#475569",
                  }}
                >
                  {isFR
                    ? "Rubrique / titre"
                    : "Matter heading / title"}
                </span>

                <select
                  className="ns-input"
                  aria-label={
                    isFR
                      ? "Choisir une rubrique"
                      : "Choose a matter heading"
                  }
                  value={
                    matterHeadingPresetValue(
                      item,
                      isFR
                    )
                  }
                  disabled={
                    saving ||
                    !canEdit
                  }
                  onChange={(event) => {
                    const value =
                      event.target.value;

                    if (
                      value &&
                      value !==
                        "__custom__"
                    ) {
                      updateTitle(
                        item.itemId,
                        value
                      );
                    }
                  }}
                >
                  {matterHeadingOptions(
                    item.type,
                    isFR
                  ).map(
                    (heading) => (
                      <option
                        key={heading}
                        value={heading}
                      >
                        {heading}
                      </option>
                    )
                  )}

                  <option value="__custom__">
                    {isFR
                      ? "Titre personnalisé…"
                      : "Custom heading…"}
                  </option>
                </select>

                <span
                  className="ns-card-meta"
                  style={{
                    fontSize: 11,
                    lineHeight: 1.4,
                  }}
                >
                  {isFR
                    ? "Choisissez une rubrique courante ou saisissez votre propre titre ci-dessous."
                    : "Choose a common book heading or enter your own title below."}
                </span>
              </>
            ) : null}

            <input
              className="ns-input"
              value={item.title}
              disabled={
                saving ||
                !canEdit
              }
              onChange={(
                event
              ) =>
                updateTitle(
                  item.itemId,
                  event.target
                    .value
                )
              }
              aria-label={
                isFR
                  ? `Titre : ${itemTypeLabel(item.type, true)}`
                  : `${itemTypeLabel(item.type, false)} title`
              }
            />
          </label>

          {canEdit ? (
            <div
              style={{
                display: "flex",
                gap: 7,
                flexWrap: "wrap",
              }}
            >
              {isMatter ? (
                <button
                  className="ns-button ns-button-primary"
                  type="button"
                  disabled={saving}
                  onClick={() =>
                    setMatterEditorItemId(
                      item.itemId
                    )
                  }
                >
                  {hasMatterContent
                    ? (
                        isFR
                          ? "Modifier le contenu"
                          : "Edit Content"
                      )
                    : (
                        isFR
                          ? "Écrire le contenu"
                          : "Write Content"
                      )}
                </button>
              ) : null}

              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  saving ||
                  !canMove(
                    item,
                    -1
                  )
                }
                onClick={() =>
                  moveItem(
                    item.itemId,
                    -1
                  )
                }
              >
                {isFR
                  ? "Plus tôt"
                  : "Move Earlier"}
              </button>

              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  saving ||
                  !canMove(
                    item,
                    1
                  )
                }
                onClick={() =>
                  moveItem(
                    item.itemId,
                    1
                  )
                }
              >
                {isFR
                  ? "Plus tard"
                  : "Move Later"}
              </button>

              {pendingRemoveId ===
              item.itemId ? (
                <>
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      setPendingRemoveId(
                        ""
                      )
                    }
                  >
                    {isFR
                      ? "Annuler"
                      : "Cancel"}
                  </button>

                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      confirmRemove(
                        item.itemId
                      )
                    }
                    style={{
                      color:
                        "#b91c1c",
                      borderColor:
                        "rgba(185,28,28,0.34)",
                    }}
                  >
                    {isFR
                      ? "Confirmer"
                      : "Confirm Remove"}
                  </button>
                </>
              ) : (
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={saving}
                  onClick={() =>
                    requestRemove(
                      item.itemId
                    )
                  }
                >
                  {isFR
                    ? "Retirer"
                    : "Remove"}
                </button>
              )}
            </div>
          ) : null}
        </div>

        {isMatter ? (
          <div
            className="ns-card-meta"
            style={{
              marginTop: 10,
              padding: "9px 11px",
              borderRadius: 11,
              background:
                hasMatterContent
                  ? "rgba(236,253,245,0.82)"
                  : "rgba(248,250,252,0.90)",
              border:
                "1px solid rgba(148,163,184,0.20)",
              color:
                hasMatterContent
                  ? "#166534"
                  : "#64748b",
              fontWeight: 750,
            }}
          >
            {hasMatterContent
              ? (
                  isFR
                    ? "Contenu enrichi prêt. Utilisez Modifier le contenu pour le mettre en forme."
                    : "Rich content is present. Use Edit Content to write or format it."
                )
              : (
                  isFR
                    ? "Aucun contenu rédigé pour cet élément."
                    : "No manuscript content has been written for this item yet."
                )}
          </div>
        ) : null}

        {item.type ===
          "part" &&
        canEdit ? (
          <div
            style={{
              marginTop: 12,
            }}
          >
            <button
              className="ns-button ns-button-outline"
              type="button"
              disabled={saving}
              onClick={() =>
                addItem(
                  "chapter",
                  item.itemId
                )
              }
            >
              {isFR
                ? "+ Ajouter un chapitre"
                : "+ Add Chapter"}
            </button>
          </div>
        ) : null}

        {item.type ===
          "chapter" ? (
          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {typeof onOpenChapter ===
            "function" ? (
              <button
                className="ns-button ns-button-primary"
                type="button"
                disabled={
                  saving ||
                  dirty
                }
                title={
                  dirty
                    ? (
                        isFR
                          ? "Enregistrez d’abord la Structure du livre."
                          : "Save Book Structure before opening this chapter."
                      )
                    : undefined
                }
                onClick={() =>
                  onOpenChapter(
                    item.itemId
                  )
                }
              >
                {canEdit
                  ? (
                      isFR
                        ? "Écrire le chapitre"
                        : "Write Chapter"
                    )
                  : (
                      isFR
                        ? "Voir le chapitre"
                        : "View Chapter"
                    )}
              </button>
            ) : null}

            {canEdit ? (
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={saving}
                onClick={() =>
                  addItem(
                    "section",
                    item.itemId
                  )
                }
              >
                {isFR
                  ? "+ Ajouter une section"
                  : "+ Add Section"}
              </button>
            ) : null}
          </div>
        ) : null}

        {children.length >
        0 ? (
          <div
            style={{
              marginTop: 12,
              marginLeft:
                depth < 2
                  ? 18
                  : 0,
              display: "grid",
              gap: 10,
            }}
          >
            {children.map(
              (child) =>
                renderItem(
                  child,
                  depth + 1
                )
            )}
          </div>
        ) : null}
      </article>
    );
  }

  function renderMainItem(
    item,
    depth = 0
  ) {
    const children =
      childrenOf(
        items,
        item.itemId
      );

    const selected =
      activeMainItemId ===
        item.itemId;

    const navigatorSelected =
      chapterNavigatorId ===
        item.itemId;

    const isChapter =
      item.type ===
        "chapter";

    const isPart =
      item.type ===
        "part";

    const chapterOrdinal =
      isChapter
        ? (
            items
              .filter(
                (candidate) =>
                  candidate.type ===
                    "chapter"
              )
              .findIndex(
                (candidate) =>
                  candidate.itemId ===
                    item.itemId
              ) + 1
          )
        : 0;

    const sectionChildren =
      isChapter
        ? children.filter(
            (child) =>
              child.type ===
                "section"
          )
        : [];

    const chapterTitle =
      clean(
        item.title
      ) ||
      (
        isFR
          ? "Chapitre sans titre"
          : "Untitled Chapter"
      );

    function renderRemoveAction(
      target
    ) {
      return pendingRemoveId ===
        target.itemId ? (
        <>
          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={saving}
            onClick={() =>
              setPendingRemoveId(
                ""
              )
            }
          >
            {isFR
              ? "Annuler"
              : "Cancel"}
          </button>

          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={saving}
            onClick={() =>
              confirmRemove(
                target.itemId
              )
            }
            style={{
              color:
                "#b91c1c",
              borderColor:
                "rgba(185,28,28,0.34)",
            }}
          >
            {isFR
              ? "Confirmer"
              : "Confirm Remove"}
          </button>
        </>
      ) : (
        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={saving}
          onClick={() =>
            requestRemove(
              target.itemId
            )
          }
        >
          {isFR
            ? "Retirer"
            : "Remove"}
        </button>
      );
    }

    if (isChapter) {
      return (
        <article
          id={
            `textbook-book-structure-chapter-${item.itemId}`
          }
          key={item.itemId}
          data-textbook-chapter-card={
            item.itemId
          }
          data-textbook-chapter-selected={
            navigatorSelected
              ? "true"
              : "false"
          }
          role="button"
          tabIndex={0}
          aria-current={
            navigatorSelected
              ? "true"
              : undefined
          }
          aria-label={
            isFR
              ? `Sélectionner le chapitre ${chapterOrdinal} : ${chapterTitle}`
              : `Select Chapter ${chapterOrdinal}: ${chapterTitle}`
          }
          onClick={(event) => {
            const interactive =
              event.target?.closest?.(
                "button, input, select, textarea, a, [contenteditable='true']"
              );

            if (interactive) {
              return;
            }

            setChapterNavigatorId(
              item.itemId
            );
          }}
          onKeyDown={(event) => {
            if (
              event.target !==
                event.currentTarget
            ) {
              return;
            }

            if (
              event.key !== "Enter" &&
              event.key !== " "
            ) {
              return;
            }

            event.preventDefault();

            setChapterNavigatorId(
              item.itemId
            );
          }}
          style={{
            marginLeft:
              depth > 0
                ? 18
                : 0,
            padding: 18,
            borderRadius: 16,
            border:
              selected ||
              navigatorSelected
                ? "2px solid rgba(5,150,105,0.48)"
                : "1.5px solid rgba(100,116,139,0.30)",
            borderLeft:
              "6px solid #047857",
            borderLeftWidth:
              navigatorSelected
                ? 8
                : 6,
            background:
              selected
                ? "rgba(240,253,244,0.78)"
                : (
                    navigatorSelected
                      ? "rgba(236,253,245,0.46)"
                      : "#ffffff"
                  ),
            boxShadow:
              selected
                ? "0 12px 28px rgba(15,118,110,0.14)"
                : (
                    navigatorSelected
                      ? "0 10px 24px rgba(15,118,110,0.11)"
                      : "0 8px 20px rgba(15,23,42,0.07)"
                  ),
            cursor: "pointer",
            scrollMarginTop: 150,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "flex-start",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                flex: "1 1 260px",
                minWidth: 0,
              }}
            >
              <div
                data-textbook-chapter-number={
                  chapterOrdinal
                }
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing:
                    "0.08em",
                  textTransform:
                    "uppercase",
                  color: "#047857",
                }}
              >
                {isFR
                  ? `CHAPITRE ${chapterOrdinal} SUR ${allChapters.length}`
                  : `CHAPTER ${chapterOrdinal} OF ${allChapters.length}`}
              </div>

              <strong
                style={{
                  display: "block",
                  marginTop: 4,
                  fontSize: 17,
                  lineHeight: 1.35,
                }}
              >
                {chapterTitle}
              </strong>

              <div
                className="ns-card-meta"
                style={{
                  marginTop: 5,
                  lineHeight: 1.4,
                }}
              >
                {sectionChildren.length ===
                1
                  ? "1 section"
                  : `${sectionChildren.length} sections`}
                {dirty
                  ? (
                      isFR
                        ? " · modifications de structure non enregistrées"
                        : " · unsaved structure changes"
                    )
                  : ""}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems:
                  "center",
              }}
            >
              {typeof onOpenChapter ===
              "function" ? (
                <button
                  data-textbook-chapter-write-action="true"
                  className="ns-button ns-button-primary"
                  type="button"
                  disabled={saving}
                  title={
                    dirty
                      ? (
                          isFR
                            ? "Enregistre d’abord la Structure du livre, puis ouvre ce chapitre."
                            : "Saves Book Structure first, then opens this Chapter."
                        )
                      : undefined
                  }
                  onClick={() =>
                    openChapterWithStructureSave(
                      item.itemId
                    )
                  }
                >
                  {dirty
                    ? (
                        isFR
                          ? "Enregistrer et écrire"
                          : "Save & Write"
                      )
                    : (
                        canEdit
                          ? (
                              isFR
                                ? "Écrire le chapitre"
                                : "Write Chapter"
                            )
                          : (
                              isFR
                                ? "Voir le chapitre"
                                : "View Chapter"
                            )
                      )}
                </button>
              ) : null}

              {canEdit ? (
                <>
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={
                      saving ||
                      !canMove(
                        item,
                        -1
                      )
                    }
                    onClick={() =>
                      moveItem(
                        item.itemId,
                        -1
                      )
                    }
                  >
                    {isFR
                      ? "Plus tôt"
                      : "Move Earlier"}
                  </button>

                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={
                      saving ||
                      !canMove(
                        item,
                        1
                      )
                    }
                    onClick={() =>
                      moveItem(
                        item.itemId,
                        1
                      )
                    }
                  >
                    {isFR
                      ? "Plus tard"
                      : "Move Later"}
                  </button>

                  {movePositionItemId ===
                  item.itemId ? (
                    <span
                      style={{
                        display:
                          "inline-flex",
                        gap: 6,
                        alignItems:
                          "center",
                        flexWrap:
                          "wrap",
                      }}
                    >
                      <input
                        className="ns-input"
                        type="number"
                        min="1"
                        max={
                          siblingItems(
                            items,
                            item
                          ).length
                        }
                        value={
                          movePositionValue
                        }
                        onChange={(event) =>
                          setMovePositionValue(
                            event.target
                              .value
                          )
                        }
                        aria-label={
                          isFR
                            ? "Nouvelle position du chapitre"
                            : "New Chapter position"
                        }
                        style={{
                          width: 82,
                          minHeight: 38,
                        }}
                      />

                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={
                          saving ||
                          !movePositionValue
                        }
                        onClick={() =>
                          moveItemToPosition(
                            item.itemId,
                            movePositionValue
                          )
                        }
                      >
                        {isFR
                          ? "Appliquer"
                          : "Apply"}
                      </button>

                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={saving}
                        onClick={() => {
                          setMovePositionItemId(
                            ""
                          );
                          setMovePositionValue(
                            ""
                          );
                        }}
                      >
                        {isFR
                          ? "Annuler"
                          : "Cancel"}
                      </button>
                    </span>
                  ) : (
                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      disabled={
                        saving ||
                        siblingItems(
                          items,
                          item
                        ).length < 2
                      }
                      onClick={() => {
                        const siblings =
                          siblingItems(
                            items,
                            item
                          );

                        const position =
                          siblings
                            .findIndex(
                              (candidate) =>
                                candidate
                                  .itemId ===
                                item.itemId
                            ) + 1;

                        setMovePositionItemId(
                          item.itemId
                        );
                        setMovePositionValue(
                          String(
                            Math.max(
                              1,
                              position
                            )
                          )
                        );
                      }}
                    >
                      {isFR
                        ? "Déplacer à la position"
                        : "Move to Position"}
                    </button>
                  )}

                  <button
                    data-textbook-chapter-edit-action="true"
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={saving}
                    aria-expanded={
                      selected
                    }
                    onClick={() =>
                      selected
                        ? finishChapterEdit(
                            item.itemId
                          )
                        : beginChapterEdit(
                            item.itemId
                          )
                    }
                  >
                    {selected
                      ? (
                          isFR
                            ? "Terminer"
                            : "Done Editing"
                        )
                      : (
                          isFR
                            ? "Modifier"
                            : "Edit"
                        )}
                  </button>

                  {selected ? (
                    <button
                      data-textbook-chapter-cancel-edit="true"
                      className="ns-button ns-button-outline"
                      type="button"
                      disabled={saving}
                      onClick={() =>
                        cancelChapterEdit(
                          item.itemId
                        )
                      }
                    >
                      {isFR
                        ? "Annuler les modifications"
                        : "Cancel Changes"}
                    </button>
                  ) : null}

                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={saving}
                    title={
                      isFR
                        ? "Duplique le chapitre et ses sections, sans copier le manuscrit."
                        : "Duplicates the Chapter and its Sections without copying manuscript writing."
                    }
                    onClick={() =>
                      duplicateChapterStructure(
                        item.itemId
                      )
                    }
                  >
                    {isFR
                      ? "Dupliquer"
                      : "Duplicate"}
                  </button>

                  {renderRemoveAction(
                    item
                  )}
                </>
              ) : null}
            </div>
          </div>

          {selected ? (
            <div
              data-textbook-chapter-structure-editor="true"
              style={{
                marginTop: 14,
                padding: 14,
                borderRadius: 13,
                border:
                  "1px solid rgba(148,163,184,0.24)",
                background:
                  "#f8fafc",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing:
                    "0.07em",
                  textTransform:
                    "uppercase",
                  color: "#475569",
                }}
              >
                {isFR
                  ? "MODIFIER LA STRUCTURE DU CHAPITRE"
                  : "EDIT CHAPTER STRUCTURE"}
              </div>

              {canEdit ? (
                <label
                  style={{
                    display: "grid",
                    gap: 5,
                    marginTop: 10,
                  }}
                >
                  <span
                    className="ns-card-meta"
                    style={{
                      fontWeight: 800,
                    }}
                  >
                    {isFR
                      ? "Titre du chapitre"
                      : "Chapter title"}
                  </span>
                  <input
                    className="ns-input"
                    value={
                      item.title
                    }
                    disabled={saving}
                    onChange={(event) =>
                      updateTitle(
                        item.itemId,
                        event.target
                          .value
                      )
                    }
                  />
                </label>
              ) : null}

              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <strong>
                  {isFR
                    ? "Sections"
                    : "Sections"}
                </strong>

                {canEdit ? (
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      addItem(
                        "section",
                        item.itemId
                      )
                    }
                  >
                    {isFR
                      ? "+ Ajouter une section"
                      : "+ Add Section"}
                  </button>
                ) : null}
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 8,
                  marginTop: 9,
                }}
              >
                {sectionChildren.length >
                0 ? (
                  sectionChildren.map(
                    (
                      section,
                      sectionIndex
                    ) => (
                      <div
                        key={
                          section.itemId
                        }
                        style={{
                          display: "flex",
                          alignItems:
                            "center",
                          gap: 8,
                          flexWrap:
                            "wrap",
                          padding:
                            "9px 10px",
                          borderRadius: 10,
                          border:
                            "1px solid rgba(148,163,184,0.22)",
                          background:
                            "#ffffff",
                        }}
                      >
                        <span
                          className="ns-card-meta"
                          style={{
                            fontWeight:
                              900,
                            minWidth: 76,
                          }}
                        >
                          {`Section ${sectionIndex + 1}`}
                        </span>

                        <input
                          className="ns-input"
                          value={
                            section.title
                          }
                          disabled={
                            saving ||
                            !canEdit
                          }
                          onChange={(event) =>
                            updateTitle(
                              section.itemId,
                              event.target
                                .value
                            )
                          }
                          aria-label={
                            isFR
                              ? `Titre de la section ${sectionIndex + 1}`
                              : `Section ${sectionIndex + 1} title`
                          }
                          style={{
                            flex:
                              "1 1 240px",
                          }}
                        />

                        {canEdit ? (
                          <>
                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              disabled={
                                saving ||
                                !canMove(
                                  section,
                                  -1
                                )
                              }
                              onClick={() =>
                                moveItem(
                                  section.itemId,
                                  -1
                                )
                              }
                            >
                              {isFR
                                ? "Plus tôt"
                                : "Move Earlier"}
                            </button>

                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              disabled={
                                saving ||
                                !canMove(
                                  section,
                                  1
                                )
                              }
                              onClick={() =>
                                moveItem(
                                  section.itemId,
                                  1
                                )
                              }
                            >
                              {isFR
                                ? "Plus tard"
                                : "Move Later"}
                            </button>

                            {renderRemoveAction(
                              section
                            )}
                          </>
                        ) : null}
                      </div>
                    )
                  )
                ) : (
                  <div
                    className="ns-card-meta"
                    style={{
                      padding:
                        "10px 0 2px",
                    }}
                  >
                    {isFR
                      ? "Aucune section. Vous pouvez écrire le chapitre directement ou ajouter des sections."
                      : "No Sections yet. You can write the Chapter directly or add Sections."}
                  </div>
                )}
              </div>

              {canEdit ? (
                <div
                  data-textbook-chapter-edit-footer="true"
                  style={{
                    marginTop: 14,
                    paddingTop: 12,
                    borderTop:
                      "1px solid rgba(148,163,184,0.24)",
                    display: "flex",
                    justifyContent:
                      "flex-end",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    className="ns-button ns-button-primary"
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      finishChapterEdit(
                        item.itemId
                      )
                    }
                  >
                    {isFR
                      ? "Terminer"
                      : "Done Editing"}
                  </button>

                  <button
                    data-textbook-chapter-cancel-edit-footer="true"
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      cancelChapterEdit(
                        item.itemId
                      )
                    }
                  >
                    {isFR
                      ? "Annuler les modifications"
                      : "Cancel Changes"}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </article>
      );
    }

    if (isPart) {
      const partChapters =
        children.filter(
          (child) =>
            child.type ===
              "chapter"
        );

      return (
        <article
          key={item.itemId}
          style={{
            marginLeft:
              depth > 0
                ? 18
                : 0,
            padding: 14,
            borderRadius: 14,
            border:
              "1px solid rgba(148,163,184,0.28)",
            background:
              "#f8fafc",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 900,
                  letterSpacing:
                    "0.07em",
                  textTransform:
                    "uppercase",
                  color: "#64748b",
                }}
              >
                {isFR
                  ? "PARTIE / UNITÉ"
                  : "PART / UNIT"}
              </div>
              <strong>
                {clean(item.title) ||
                  (
                    isFR
                      ? "Sans titre"
                      : "Untitled"
                  )}
              </strong>
              <div
                className="ns-card-meta"
                style={{
                  marginTop: 3,
                }}
              >
                {partChapters.length}{" "}
                {partChapters.length === 1
                  ? (
                      isFR
                        ? "chapitre"
                        : "Chapter"
                    )
                  : (
                      isFR
                        ? "chapitres"
                        : "Chapters"
                    )}
              </div>
            </div>

            {canEdit ? (
              <div
                style={{
                  display: "flex",
                  gap: 7,
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={saving}
                  onClick={() =>
                    setActiveMainItemId(
                      selected
                        ? ""
                        : item.itemId
                    )
                  }
                >
                  {selected
                    ? (
                        isFR
                          ? "Fermer"
                          : "Done"
                      )
                    : (
                        isFR
                          ? "Modifier"
                          : "Edit"
                      )}
                </button>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    saving ||
                    !canMove(
                      item,
                      -1
                    )
                  }
                  onClick={() =>
                    moveItem(
                      item.itemId,
                      -1
                    )
                  }
                >
                  {isFR
                    ? "Plus tôt"
                    : "Move Earlier"}
                </button>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    saving ||
                    !canMove(
                      item,
                      1
                    )
                  }
                  onClick={() =>
                    moveItem(
                      item.itemId,
                      1
                    )
                  }
                >
                  {isFR
                    ? "Plus tard"
                    : "Move Later"}
                </button>

                {renderRemoveAction(
                  item
                )}
              </div>
            ) : null}
          </div>

          {selected &&
          canEdit ? (
            <div
              style={{
                marginTop: 10,
                display: "grid",
                gap: 9,
              }}
            >
              <input
                className="ns-input"
                value={item.title}
                disabled={saving}
                onChange={(event) =>
                  updateTitle(
                    item.itemId,
                    event.target.value
                  )
                }
              />

              <div>
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={saving}
                  onClick={() =>
                    addItem(
                      "chapter",
                      item.itemId
                    )
                  }
                >
                  {isFR
                    ? "+ Ajouter un chapitre"
                    : "+ Add Chapter"}
                </button>
              </div>
            </div>
          ) : null}

          {partChapters.length >
          0 ? (
            <div
              style={{
                display: "grid",
                gap: 10,
                marginTop: 12,
              }}
            >
              {partChapters.map(
                (child) =>
                  renderMainItem(
                    child,
                    depth + 1
                  )
              )}
            </div>
          ) : null}
        </article>
      );
    }

    return null;
  }

  function renderMainArea({
    title,
    body,
    rows,
    actions,
  }) {
    return (
      <section
        className="ns-card"
        data-textbook-area="main-book"
        style={{
          padding: 20,
          borderRadius: 18,
          border:
            "2px solid rgba(5,150,105,0.30)",
          background:
            "linear-gradient(180deg, rgba(236,253,245,0.62) 0%, rgba(255,255,255,0.98) 30%)",
          boxShadow:
            "0 8px 24px rgba(15,118,110,0.08)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems:
              "flex-start",
            justifyContent:
              "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              maxWidth: 760,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing:
                  "0.09em",
                textTransform:
                  "uppercase",
                color: "#047857",
                marginBottom: 5,
              }}
            >
              {isFR
                ? "STRUCTURE DU LIVRE PRINCIPAL"
                : "MAIN BOOK STRUCTURE"}
            </div>

            <h4
              className="ns-card-title ns-textbook-academic-title"
              style={{
                margin: "0 0 5px",
                fontSize: 22,
              }}
            >
              {title}
            </h4>

            <p
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.5,
              }}
            >
              {body}
            </p>
          </div>

          {canEdit ? (
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {actions}
            </div>
          ) : null}
        </div>

        {allChapters.length > 1 ? (
          <div
            data-textbook-book-structure-chapter-navigator="true"
            style={{
              marginTop: 16,
              padding: "12px 14px",
              borderRadius: 13,
              border:
                "1px solid rgba(5,150,105,0.24)",
              background:
                "rgba(248,250,252,0.98)",
              display: "grid",
              gap: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 900,
                    letterSpacing:
                      "0.08em",
                    textTransform:
                      "uppercase",
                    color: "#047857",
                  }}
                >
                  {isFR
                    ? "NAVIGATEUR DES CHAPITRES"
                    : "CHAPTER NAVIGATOR"}
                </div>
                <strong>
                  {isFR
                    ? `${allChapters.length} chapitres dans le livre`
                    : `${allChapters.length} Chapters in this book`}
                </strong>
              </div>

              <span
                className="ns-card-meta"
              >
                {isFR
                  ? "Tous les chapitres restent ci-dessous dans l’ordre du livre."
                  : "All Chapters remain below in book order."}
              </span>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "auto minmax(240px, 1fr) auto",
                gap: 10,
                alignItems: "end",
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  currentNavigatorIndex <=
                  0
                }
                onClick={() =>
                  stepBookStructureChapter(
                    -1
                  )
                }
              >
                {isFR
                  ? "Chapitre précédent"
                  : "Previous Chapter"}
              </button>

              <label
                style={{
                  display: "grid",
                  gap: 5,
                  fontWeight: 800,
                }}
              >
                <span>
                  {isFR
                    ? "Aller à un chapitre"
                    : "Jump to Chapter"}
                </span>

                <select
                  data-textbook-book-structure-chapter-jump="true"
                  aria-label={
                    isFR
                      ? "Aller à un chapitre"
                      : "Jump to Chapter"
                  }
                  value={
                    chapterNavigatorId ||
                    allChapters[0]
                      ?.itemId ||
                    ""
                  }
                  onChange={(event) =>
                    jumpToBookStructureChapter(
                      clean(
                        event.target
                          .value
                      )
                    )
                  }
                  style={{
                    minHeight: 42,
                    padding:
                      "9px 11px",
                    borderRadius: 9,
                    border:
                      "1px solid #cbd5e1",
                    background: "#fff",
                    font: "inherit",
                  }}
                >
                  {allChapters.map(
                    (
                      chapter,
                      index
                    ) => (
                      <option
                        key={
                          chapter.itemId
                        }
                        value={
                          chapter.itemId
                        }
                      >
                        {chapterNavigatorOptionLabel(
                          chapter,
                          index,
                          isFR
                        )}
                      </option>
                    )
                  )}
                </select>
              </label>

              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  currentNavigatorIndex <
                    0 ||
                  currentNavigatorIndex >=
                    allChapters.length -
                      1
                }
                onClick={() =>
                  stepBookStructureChapter(
                    1
                  )
                }
              >
                {isFR
                  ? "Chapitre suivant"
                  : "Next Chapter"}
              </button>
            </div>
          </div>
        ) : null}

        {dirty ? (
          <div
            data-textbook-structure-unsaved-note="true"
            role="status"
            style={{
              marginTop: 14,
              padding: "10px 12px",
              borderRadius: 11,
              border:
                "1px solid rgba(217,119,6,0.28)",
              background: "#fff7ed",
              color: "#9a3412",
              lineHeight: 1.45,
            }}
          >
            <strong>
              {isFR
                ? "Modifications de structure non enregistrées."
                : "Unsaved Book Structure changes."}
            </strong>{" "}
            {isFR
              ? "Enregistrez ci-dessous, ou utilisez « Enregistrer et écrire » sur un chapitre."
              : "Save below, or use Save & Write on any Chapter."}
          </div>
        ) : null}

        <div
          style={{
            marginTop: 18,
            display: "grid",
            gap: 20,
          }}
        >
          {rows.length > 0
            ? rows.map(
                (item) =>
                  renderMainItem(
                    item
                  )
              )
            : (
              <div
                className="ns-card"
                style={{
                  padding: 16,
                  borderRadius: 14,
                  border:
                    "1px dashed rgba(5,150,105,0.34)",
                  background:
                    "rgba(255,255,255,0.82)",
                }}
              >
                <strong>
                  {isFR
                    ? "Commencez par le premier chapitre."
                    : "Start with the first chapter."}
                </strong>
                <div
                  className="ns-card-meta"
                  style={{
                    marginTop: 4,
                  }}
                >
                  {isFR
                    ? "Le Livre principal reste votre espace de travail central."
                    : "Main Book stays open as your central working area."}
                </div>
              </div>
            )}
        </div>
      </section>
    );
  }

  function renderSupportingArea({
    areaKey,
    title,
    body,
    rows,
    editing,
    setEditing,
    areaDirty,
    addType,
  }) {
    const isFront =
      areaKey ===
        "front-matter";

    const savedCount =
      rows.length;

    const compactSummary =
      rows
        .map((item) =>
          clean(
            item.title
          )
        )
        .filter(Boolean)
        .slice(0, 3)
        .join(" · ");

    const editLabel =
      savedCount > 0
        ? (
            canEdit
              ? (
                  isFR
                    ? `Modifier ${title}`
                    : `Edit ${title}`
                )
              : (
                  isFR
                    ? `Voir ${title}`
                    : `View ${title}`
                )
          )
        : (
            isFR
              ? `Préparer ${title}`
              : `Set Up ${title}`
          );

    return (
      <section
        className="ns-card ns-card-soft"
        data-textbook-area={
          areaKey
        }
        style={{
          padding:
            editing
              ? 18
              : "13px 16px",
          borderRadius: 16,
          border:
            "1px solid rgba(100,116,139,0.24)",
          background:
            isFront
              ? "rgba(248,250,252,0.96)"
              : "rgba(241,245,249,0.82)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems:
              "flex-start",
            justifyContent:
              "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              flex: "1 1 460px",
              minWidth: 0,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 900,
                letterSpacing:
                  "0.09em",
                textTransform:
                  "uppercase",
                color: "#64748b",
                marginBottom: 3,
              }}
            >
              {isFront
                ? (
                    isFR
                      ? "AVANT LE LIVRE PRINCIPAL"
                      : "BEFORE THE MAIN BOOK"
                  )
                : (
                    isFR
                      ? "APRÈS LE LIVRE PRINCIPAL"
                      : "AFTER THE MAIN BOOK"
                  )}
              {" · "}
              {isFR
                ? "CONTENU D’APPUI"
                : "SUPPORTING MATTER"}
            </div>

            <h4
              className="ns-card-title ns-textbook-academic-title"
              style={{
                margin: 0,
                fontSize:
                  editing
                    ? 19
                    : 17,
              }}
            >
              {title}
            </h4>

            {editing ? (
              <p
                className="ns-card-meta"
                style={{
                  margin:
                    "5px 0 0",
                  lineHeight: 1.45,
                }}
              >
                {body}
              </p>
            ) : (
              <div
                className="ns-card-meta"
                style={{
                  marginTop: 4,
                  lineHeight: 1.4,
                }}
              >
                {savedCount > 0
                  ? (
                      <>
                        <strong>
                          {savedCount === 1
                            ? (
                                isFR
                                  ? "1 élément enregistré"
                                  : "1 saved item"
                              )
                            : (
                                isFR
                                  ? `${savedCount} éléments enregistrés`
                                  : `${savedCount} saved items`
                              )}
                        </strong>
                        {compactSummary
                          ? ` · ${compactSummary}`
                          : ""}
                      </>
                    )
                  : (
                      isFR
                        ? "Pas encore configuré. Il reste discret jusqu’à ce que vous en ayez besoin."
                        : "Not set up yet. It stays out of the way until you need it."
                    )}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems:
                "center",
            }}
          >
            {!editing ? (
              (
                canEdit ||
                savedCount > 0
              ) ? (
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={saving}
                  aria-expanded="false"
                  onClick={() =>
                    setEditing(true)
                  }
                >
                  {editLabel}
                </button>
              ) : null
            ) : (
              <>
                {canEdit ? (
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      addItem(
                        addType
                      )
                    }
                  >
                    {isFront
                      ? (
                          isFR
                            ? "+ Ajouter des pages liminaires"
                            : "+ Add Front Matter"
                        )
                      : (
                          isFR
                            ? "+ Ajouter un élément de fin"
                            : "+ Add Back Matter"
                        )}
                  </button>
                ) : null}

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    saving ||
                    areaDirty
                  }
                  title={
                    areaDirty
                      ? (
                          isFR
                            ? "Enregistrez d’abord la Structure du livre."
                            : "Save Book Structure before hiding this area."
                        )
                      : undefined
                  }
                  aria-expanded="true"
                  onClick={() =>
                    setEditing(false)
                  }
                >
                  {isFront
                    ? (
                        isFR
                          ? "Masquer les pages liminaires"
                          : "Hide Front Matter"
                      )
                    : (
                        isFR
                          ? "Masquer les éléments de fin"
                          : "Hide Back Matter"
                      )}
                </button>
              </>
            )}
          </div>
        </div>

        {editing ? (
          <div
            style={{
              marginTop: 14,
              display: "grid",
              gap: 10,
            }}
          >
            {rows.length > 0
              ? rows.map(
                  (item) =>
                    renderItem(
                      item
                    )
                )
              : (
                <div
                  className="ns-card"
                  style={{
                    padding: 14,
                    borderRadius: 14,
                    border:
                      "1px dashed rgba(100,116,139,0.34)",
                    background:
                      "rgba(255,255,255,0.76)",
                  }}
                >
                  <span
                    className="ns-card-meta"
                  >
                    {isFR
                      ? "Rien ici pour le moment."
                      : "Nothing here yet."}
                  </span>
                </div>
              )}
          </div>
        ) : null}
      </section>
    );
  }

  const matterEditorItem =
    items.find(
      (item) =>
        item.itemId ===
          matterEditorItemId &&
        [
          "front_matter",
          "back_matter",
        ].includes(
          item.type
        )
    ) ||
    null;

  function renderMatterEditor() {
    if (!matterEditorItem) {
      return null;
    }

    const front =
      matterEditorItem.type ===
        "front_matter";

    return (
      <div
        data-textbook-floating-editor-backdrop="book-matter"
        role="presentation"
        onMouseDown={(event) => {
          if (
            event.target ===
            event.currentTarget
          ) {
            setMatterEditorItemId(
              ""
            );
          }
        }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1200,
          display: "grid",
          placeItems: "center",
          padding: 20,
          background:
            "rgba(15,23,42,0.52)",
        }}
      >
        <div
          data-textbook-floating-editor="book-matter"
          role="dialog"
          aria-modal="true"
          aria-label={
            front
              ? (
                  isFR
                    ? "Modifier les pages liminaires"
                    : "Edit Front Matter"
                )
              : (
                  isFR
                    ? "Modifier les éléments de fin"
                    : "Edit Back Matter"
                )
          }
          style={{
            width:
              "min(900px, 95vw)",
            maxHeight: "88vh",
            overflowY: "auto",
            padding: 20,
            borderRadius: 20,
            border:
              "1px solid rgba(5,150,105,0.30)",
            background:
              "#ffffff",
            boxShadow:
              "0 30px 90px rgba(15,23,42,0.32)",
            display: "grid",
            gap: 14,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing:
                  "0.1em",
                textTransform:
                  "uppercase",
                color: "#047857",
              }}
            >
              {front
                ? (
                    isFR
                      ? "PAGES LIMINAIRES"
                      : "FRONT MATTER"
                  )
                : (
                    isFR
                      ? "ÉLÉMENTS DE FIN"
                      : "BACK MATTER"
                  )}
            </div>

            <h4
              className="ns-textbook-academic-title"
              style={{
                margin:
                  "5px 0 4px",
                fontSize: 24,
              }}
            >
              {matterEditorItem.title}
            </h4>

            <p
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.5,
              }}
            >
              {isFR
                ? "Rédigez et mettez en forme ce contenu comme le texte principal. Fermez cette fenêtre, puis enregistrez la Structure du livre pour conserver les modifications."
                : "Write and format this content like the main manuscript. Close this window, then Save Book Structure to keep the changes."}
            </p>
          </div>

          <div
            className="ns-textbook-book-matter-editor"
          >
            <ChildrensBookManuscriptEditor
              lang={
                isFR
                  ? "fr"
                  : "en"
              }
              entry={{
                unitId:
                  matterEditorItem
                    .itemId,

                ...(
                  matterEditorItem
                    .manuscriptContent &&
                  typeof matterEditorItem
                    .manuscriptContent ===
                    "object" &&
                  !Array.isArray(
                    matterEditorItem
                      .manuscriptContent
                  )
                    ? {
                        manuscriptContent:
                          matterEditorItem
                            .manuscriptContent,
                      }
                    : {
                        manuscriptText:
                          "",
                      }
                ),
              }}
              disabled={
                saving ||
                !canEdit
              }
              compactToolbar={true}
              stickyToolbar={true}
              enableWholeDocumentJustify={true}
              onChange={(value) =>
                updateMatterContent(
                  matterEditorItem
                    .itemId,
                  value
                )
              }
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent:
                "flex-end",
            }}
          >
            <button
              className="ns-button ns-button-primary"
              type="button"
              onClick={() =>
                setMatterEditorItemId(
                  ""
                )
              }
            >
              {isFR
                ? "Terminer la modification"
                : "Done Editing"}
            </button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <form
      className="ns-card"
      onSubmit={submit}
      style={{
        padding: 22,
        borderRadius: 20,
        border:
          "1.5px solid rgba(5,150,105,0.24)",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 900,
            letterSpacing:
              "0.1em",
            textTransform:
              "uppercase",
            color: "#047857",
          }}
        >
          {isFR
            ? "STRUCTURE DU LIVRE"
            : "BOOK STRUCTURE"}
        </div>

        <h3
          className="ns-card-title ns-textbook-academic-title"
          style={{
            margin: "6px 0 8px",
            fontSize: 26,
          }}
        >
          {clean(
            project?.title
          ) ||
            (
              isFR
                ? "Manuel sans titre"
                : "Untitled Textbook"
            )}
        </h3>

        <p
          className="ns-card-meta"
          style={{
            margin: 0,
            lineHeight: 1.6,
            maxWidth: 760,
          }}
        >
          {isFR
            ? "Décidez simplement de ce qui appartient au manuel et dans quel ordre. Le contenu détaillé des chapitres viendra plus tard."
            : "Decide what belongs in your textbook and in what order. Detailed chapter writing comes later."}
        </p>
      </div>

      {!canEdit ? (
        <div
          className="ns-card ns-card-soft"
          role="status"
          style={{
            marginTop: 18,
            padding: 14,
            borderRadius: 14,
          }}
        >
          <strong>
            {isFR
              ? "Lecture seule"
              : "Read only"}
          </strong>

          <div
            className="ns-card-meta"
            style={{
              marginTop: 4,
            }}
          >
            {isFR
              ? "Vous pouvez consulter la Structure du livre. Seul le propriétaire peut la modifier dans cette version."
              : "You can review Book Structure. Only the project owner can edit it in this version."}
          </div>
        </div>
      ) : null}

      {canEdit &&
      typeof onPreviewManuscriptImport ===
        "function" ? (
        <section
          className="ns-card ns-card-soft"
          data-textbook-word-import-preview="true"
          style={{
            marginTop: 18,
            padding: 16,
            borderRadius: 16,
            border:
              "1px solid rgba(5,150,105,0.22)",
            background:
              "rgba(240,253,244,0.72)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ maxWidth: 700 }}>
              <strong>
                {isFR
                  ? "Importer un manuscrit Word"
                  : "Import a Word Manuscript"}
              </strong>

              <div
                className="ns-card-meta"
                style={{
                  marginTop: 4,
                  lineHeight: 1.5,
                }}
              >
                {isFR
                  ? "Choisissez un fichier DOCX. NextScenes prévisualisera les chapitres et sections détectés avant toute modification."
                  : "Choose a DOCX file. NextScenes will preview the detected Chapters and Sections before making any changes."}
              </div>
            </div>

            <span
              className="ns-card-meta"
              style={{ fontWeight: 800 }}
            >
              {manuscriptImportPlan
                ? (
                    isFR
                      ? "2 · Vérifier"
                      : "2 · Review"
                  )
                : (
                    isFR
                      ? "1 · Prévisualiser"
                      : "1 · Preview"
                  )}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
              marginTop: 14,
            }}
          >
            <input
              className="ns-input"
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              disabled={
                manuscriptImportPreviewing
              }
              onChange={
                chooseWordManuscript
              }
              aria-label={
                isFR
                  ? "Choisir un manuscrit Word DOCX"
                  : "Choose DOCX Word manuscript"
              }
              style={{ maxWidth: 420 }}
            />

            <button
              className="ns-button ns-button-outline"
              type="button"
              disabled={
                manuscriptImportPreviewing ||
                !manuscriptImportFile
              }
              onClick={
                previewWordManuscript
              }
            >
              {manuscriptImportPreviewing
                ? (
                    isFR
                      ? "Lecture…"
                      : "Reading…"
                  )
                : (
                    isFR
                      ? "Prévisualiser le manuscrit"
                      : "Preview Manuscript"
                  )}
            </button>


              </div>

          {manuscriptImportError ? (
            <div
              role="alert"
              style={{
                marginTop: 12,
                color: "#b91c1c",
                fontWeight: 800,
              }}
            >
              {manuscriptImportError}
            </div>
          ) : null}

          {manuscriptImportPlan ? (
            <div
              style={{
                marginTop: 14,
                padding: 14,
                borderRadius: 14,
                border:
                  "1px solid rgba(5,150,105,0.18)",
                background: "#ffffff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <strong>
                  {isFR
                    ? "Structure détectée"
                    : "Detected Structure"}
                </strong>

                <span
                  className="ns-card-meta"
                  style={{ fontWeight: 800 }}
                >
                  {manuscriptImportPlan?.summary?.chapterCount || 0}{" "}
                  {isFR ? "chapitre(s)" : "chapter(s)"}{" "}
                  ·{" "}
                  {manuscriptImportPlan?.summary?.sectionCount || 0}{" "}
                  {isFR ? "section(s)" : "section(s)"}
                </span>
              </div>

              <div
                className="ns-card-meta"
                style={{
                  marginTop: 5,
                  lineHeight: 1.5,
                }}
              >
                {isFR
                  ? "Rien n’a encore été ajouté ou enregistré dans votre manuel. Vérifiez la structure ci-dessous ; seul « Ajouter le manuscrit » modifiera votre manuel."
                  : "Nothing has been added to or saved in your textbook yet. Review the structure below; only Add Manuscript changes your textbook."}
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 7,
                  marginTop: 12,
                }}
              >
                {manuscriptImportItems.map(
                  (item) => (
                    <div
                      key={item.itemId}
                      style={{
                        marginLeft:
                          item.type === "section"
                            ? 22
                            : 0,
                        padding: "8px 10px",
                        borderRadius: 10,
                        background:
                          item.type === "section"
                            ? "#f8fafc"
                            : "#f0fdf4",
                        border:
                          "1px solid rgba(148,163,184,0.22)",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 900,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          color:
                            item.type === "chapter"
                              ? "#047857"
                              : "#475569",
                        }}
                      >
                        {item.type === "chapter"
                          ? (isFR ? "Chapitre" : "Chapter")
                          : "Section"}
                      </span>

                      <span
                        style={{
                          marginLeft: 8,
                          fontWeight: 800,
                        }}
                      >
                        {item.title}
                      </span>
                    </div>
                  )
                )}
              </div>

              {manuscriptImportPlan?.leadingContent ? (
                <div
                  role="status"
                  style={{
                    marginTop: 12,
                    padding: 11,
                    borderRadius: 11,
                    border:
                      "1px solid rgba(217,119,6,0.28)",
                    background: "#fff7ed",
                    color: "#9a3412",
                    fontWeight: 700,
                    lineHeight: 1.45,
                  }}
                >
                  {isFR
                    ? "Du contenu apparaît avant le premier chapitre détecté. Vous choisirez où il appartient avant d’appliquer l’importation."
                    : "Some content appears before the first detected Chapter. You will choose where it belongs before the import is applied."}
                </div>
              ) : null}

              {Array.isArray(
                manuscriptImportResult?.warnings
              ) &&
              manuscriptImportResult.warnings.length > 0 ? (
                <div
                  className="ns-card-meta"
                  style={{
                    marginTop: 10,
                    lineHeight: 1.45,
                  }}
                >
                  {manuscriptImportResult.warnings.join(" ")}
                </div>
              ) : null}

              <div
                style={{
                  marginTop: 14,
                  paddingTop: 14,
                  borderTop:
                    "1px solid rgba(148,163,184,0.22)",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  className="ns-button ns-button-primary"
                  onClick={
                    applyWordManuscriptImport
                  }
                  disabled={
                    !canEdit ||
                    saving ||
                    dirty ||
                    revision < 1 ||
                    manuscriptImportPlan
                      ?.ready !== true ||
                    Boolean(
                      manuscriptImportPlan
                        ?.leadingContent
                    ) ||
                    typeof onApplyManuscriptImport !==
                      "function"
                  }
                  title={
                    dirty
                      ? (
                          isFR
                            ? "Enregistrez d’abord vos modifications actuelles de la Structure du livre."
                            : "Save your current Book Structure changes first."
                        )
                      : (
                          manuscriptImportPlan
                            ?.leadingContent
                            ? (
                                isFR
                                  ? "Décidez d’abord où placer le contenu qui précède le premier chapitre."
                                  : "Decide where the content before the first Chapter belongs first."
                              )
                            : ""
                        )
                  }
                >
                  {isFR
                    ? "Ajouter le manuscrit"
                    : "Add Manuscript"}
                </button>

                <span
                  className="ns-card-meta"
                  style={{
                    maxWidth: 430,
                    lineHeight: 1.45,
                  }}
                >
                  {isFR
                    ? "Ajoute les chapitres détectés au Livre principal. Votre structure et vos écrits existants sont conservés."
                    : "Adds the detected Chapters to the Main Book. Your existing structure and writing are kept."}
                </span>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      <div
        style={{
          marginTop: 20,
          display: "grid",
          gap: 16,
        }}
      >
        {renderSupportingArea({
          areaKey:
            "front-matter",
          title:
            isFR
              ? "Pages liminaires"
              : "Front Matter",
          body:
            isFR
              ? "Ajoutez seulement les éléments dont ce livre a besoin avant le premier chapitre."
              : "Add only what this book needs before the first chapter.",
          rows:
            frontMatter,
          editing:
            frontMatterEditing,
          setEditing:
            setFrontMatterEditing,
          areaDirty:
            frontMatterDirty,
          addType:
            "front_matter",
        })}

        {renderMainArea({
          title:
            isFR
              ? "Planifier les chapitres"
              : "Plan Your Chapters",
          body:
            isFR
              ? "Construisez normalement le manuel chapitre par chapitre. Utilisez une Partie / Unité seulement lorsqu’un groupe de chapitres doit réellement rester ensemble. Cliquez sur un chapitre pour le renommer, le déplacer, le dupliquer, ajouter des sections ou commencer la rédaction."
              : "Normally build the textbook one Chapter at a time. Use a Part / Unit only when a group of Chapters genuinely belongs together. Click a Chapter to rename, move, duplicate, add Sections, or start writing.",
          rows:
            mainRoots,
          actions: (
            <>
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={saving}
                onClick={() =>
                  addItem(
                    "chapter"
                  )
                }
              >
                {isFR
                  ? "+ Ajouter un chapitre — recommandé"
                  : "+ Add Chapter — Recommended"}
              </button>

              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={saving}
                onClick={() =>
                  addItem(
                    "part"
                  )
                }
              >
                {isFR
                  ? "+ Ajouter une Partie / Unité — facultatif"
                  : "+ Add Part / Unit — Optional"}
              </button>
            </>
          ),
        })}

        {renderSupportingArea({
          areaKey:
            "back-matter",
          title:
            isFR
              ? "Éléments de fin"
              : "Back Matter",
          body:
            isFR
              ? "Ajoutez le glossaire, la bibliographie, les annexes, l’index ou tout autre élément nécessaire après le contenu principal."
              : "Add a glossary, bibliography, appendices, index or anything else needed after the main content.",
          rows:
            backMatter,
          editing:
            backMatterEditing,
          setEditing:
            setBackMatterEditing,
          areaDirty:
            backMatterDirty,
          addType:
            "back_matter",
        })}
      </div>

      {saveError ? (
        <p
          role="alert"
          style={{
            margin:
              "16px 0 0",
            color: "#b91c1c",
            fontWeight: 800,
          }}
        >
          {saveError}
        </p>
      ) : null}

      {saveMessage ? (
        <p
          role="status"
          style={{
            margin:
              "16px 0 0",
            color: "#047857",
            fontWeight: 900,
          }}
        >
          {saveMessage}
        </p>
      ) : null}

      {renderMatterEditor()}

      {canEdit ? (
        <div
          style={{
            marginTop: 18,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <button
            className="ns-button ns-button-primary"
            type="submit"
            disabled={
              saving ||
              !dirty ||
              revision < 1
            }
          >
            {saving
              ? (
                  isFR
                    ? "Enregistrement…"
                    : "Saving…"
                )
              : (
                  isFR
                    ? "Enregistrer la Structure du livre"
                    : "Save Book Structure"
                )}
          </button>

          {!dirty &&
          !saveMessage ? (
            <span
              className="ns-card-meta"
            >
              {isFR
                ? "Aucune modification non enregistrée."
                : "No unsaved changes."}
            </span>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}
